import { gql } from '@apollo/client';

/**
 * 看板管理页的 GraphQL 契约。
 *
 * 注意：管理端**没有「一次拉全部看板」的查询**。按分组折叠 + 展开某组才查该组
 * （`DATA_PORTAL_PAGES_BY_GROUP`），加载量与分组数而不是看板数成正比；
 * 搜索/状态筛选走 `DATA_PORTAL_SEARCH_PAGES`（后端过滤 + 有条数上限）。
 */

/** 分组列表，含组内已启用看板数 */
export const GET_DATA_PORTAL_GROUPS = gql`
  query DataPortalGroups {
    dataPortalGroups {
      id
      portalCode
      nameEn
      nameZh
      nameEs
      sortOrder
      enabledPageCount
      updatedBy
      updatedAt
    }
  }
`;

/** 页头概览三个数字，走独立 COUNT 聚合，不拉数据行 */
export const GET_DATA_PORTAL_OVERVIEW = gql`
  query DataPortalOverview {
    dataPortalOverview {
      total
      enabledCount
      ungroupedCount
      ungroupedByPortal {
        portalCode
        count
      }
    }
  }
`;

const PAGE_FIELDS = `
  id
  pageKey
  portalCode
  groupId
  nameEn
  nameZh
  nameEs
  embedPath
  icon
  sortOrder
  visibleScope
  visibleRoles
  visibleAccountKey
  visibleAccounts
  restrictConfigVisible
  openMode
  enabled
  createdBy
  updatedBy
  lastOperatorAccountId
`;

/** 展开某个分组时才调；groupId 传 null 取未分组 */
export const DATA_PORTAL_PAGES_BY_GROUP = gql`
  query DataPortalPagesByGroup($groupId: ID, $keyword: String, $enabled: Boolean, $portalCode: String) {
    dataPortalPagesByGroup(
      groupId: $groupId
      keyword: $keyword
      enabled: $enabled
      portalCode: $portalCode
    ) {
      ${PAGE_FIELDS}
    }
  }
`;

/** 搜索/筛选态用：跨分组查询命中项 */
export const DATA_PORTAL_SEARCH_PAGES = gql`
  query DataPortalSearchPages($keyword: String, $enabled: Boolean) {
    dataPortalSearchPages(keyword: $keyword, enabled: $enabled) {
      ${PAGE_FIELDS}
    }
  }
`;

/**
 * 可选来源门户，取自后端 data-portal.portals 配置；新增门户只加配置，前端自动出现选项。
 * **已按 sortOrder 降序排好**，直接按此顺序渲染门户分段，不要在前端再排一遍。
 * 展示名走 i18n `enum.dataPortalSource.<CODE>`（见 pickPortalName）。
 */
export const GET_PORTAL_CODES = gql`
  query DataPortalPortalCodes {
    dataPortalPortalCodes
  }
`;

/** 姓名**完全相等**搜索启用中的账号。输入过程中不要调，必须点搜索/回车才调 */
export const SEARCH_ACCOUNTS = gql`
  query DataPortalSearchAccounts($keyword: String!, $byPhone: Boolean!) {
    dataPortalSearchAccounts(keyword: $keyword, byPhone: $byPhone) {
      id
      name
      phone
      role
      enabled
    }
  }
`;

/** 编辑弹窗回显已选人员的姓名（库里只存 ID）；含已停用账号 */
export const ACCOUNTS_BY_IDS = gql`
  query DataPortalAccountsByIds($ids: [ID!]!) {
    dataPortalAccountsByIds(ids: $ids) {
      id
      name
      phone
      role
      enabled
    }
  }
`;

export const CREATE_DATA_PORTAL_PAGE = gql`
  mutation CreateDataPortalPage($input: DataPortalPageInput!) {
    createDataPortalPage(input: $input) {
      id
    }
  }
`;

export const UPDATE_DATA_PORTAL_PAGE = gql`
  mutation UpdateDataPortalPage($input: DataPortalPageInput!) {
    updateDataPortalPage(input: $input) {
      id
    }
  }
`;

export const DELETE_DATA_PORTAL_PAGE = gql`
  mutation DeleteDataPortalPage($id: ID!) {
    deleteDataPortalPage(id: $id)
  }
`;

/** 单个移动与批量移动共用；groupId 不传即退回未分组 */
export const MOVE_PAGES_TO_GROUP = gql`
  mutation MoveDataPortalPagesToGroup($ids: [ID!]!, $groupId: ID) {
    moveDataPortalPagesToGroup(ids: $ids, groupId: $groupId)
  }
`;

export const CREATE_DATA_PORTAL_GROUP = gql`
  mutation CreateDataPortalGroup($input: DataPortalGroupInput!) {
    createDataPortalGroup(input: $input) {
      id
    }
  }
`;

export const UPDATE_DATA_PORTAL_GROUP = gql`
  mutation UpdateDataPortalGroup($input: DataPortalGroupInput!) {
    updateDataPortalGroup(input: $input) {
      id
    }
  }
`;

export const DELETE_DATA_PORTAL_GROUP = gql`
  mutation DeleteDataPortalGroup($id: ID!) {
    deleteDataPortalGroup(id: $id)
  }
`;

/** 一次提交整序，不做相邻互换（存量 sortOrder 全是 0 时互换换不动） */
export const REORDER_DATA_PORTAL_GROUPS = gql`
  mutation ReorderDataPortalGroups($ids: [ID!]!) {
    reorderDataPortalGroups(ids: $ids)
  }
`;

export interface DataPortalPageType {
  id: string;
  pageKey: string;
  /** 来源门户标识，如 BA=商分门户 */
  portalCode: string;
  /** 所属分组ID，null=未分组 */
  groupId?: string | null;
  nameEn: string;
  nameZh: string;
  nameEs: string;
  /** 库里存的是不含域名的路径，如 /share/<token>/embed */
  embedPath: string;
  icon?: string | null;
  sortOrder: number;
  visibleScope: 'ALL' | 'ROLE' | 'ACCOUNT';
  visibleRoles: string[];
  visibleAccountKey: 'ACCOUNT_ID' | 'PHONE';
  visibleAccounts: string[];
  /** 限制配置人员可见：true=严格按可见范围 */
  restrictConfigVisible: boolean;
  openMode: string;
  enabled: boolean;
  /** 创建人名称 */
  createdBy?: string | null;
  /** 最后修改人名称 */
  updatedBy?: string | null;
  /** 最后操作人账号ID */
  lastOperatorAccountId?: string | null;
}

export interface DataPortalGroupType {
  id: string;
  /** 所属来源门户 code。分组绑定唯一门户，创建后不可改 */
  portalCode: string;
  nameEn: string;
  nameZh: string;
  nameEs: string;
  sortOrder: number;
  /** 组内**已启用**看板数；管理页组内条数含停用，两者不同是设计要求 */
  enabledPageCount: number;
  updatedBy?: string | null;
  updatedAt?: number | null;
}

export interface DataPortalAccountType {
  id: string;
  name: string;
  phone?: string | null;
  role: string;
  /** 搜索结果恒为 true；已配置列表里可能出现 false（已停用），标灰且只允许移除 */
  enabled: boolean;
}

export interface DataPortalOverviewType {
  total: number;
  enabledCount: number;
  ungroupedCount: number;
  /** 按门户拆分的未分组看板数；某门户没有未分组看板时不出现在数组里，查不到即视作 0 */
  ungroupedByPortal: { portalCode: string; count: number }[];
}
